import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const SAPEnterpriseSystems = () => {
  const phrases = [
    "S/4HANA Core Migrations",
    "BTP Integration Matrices",
    "Clean Core ABAP Development"
  ];

  const cardData = [
    { type: 'sap-core', content: '💻' },
    { type: 'amber-wave', content: '📊' },
    { type: 'btp-stack', content: '⚙️' },
    { type: 'hana-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "💻", title: "S/4HANA Migration & Transformation", desc: "Design and execute seamless greenfield implementations or brownfield landscape conversions from legacy ECC instances to high-performance SAP S/4HANA environments." },
    { icon: "⚙️", title: "SAP Business Technology Platform (BTP)", desc: "Build side-by-side extensions, develop custom cloud apps, and configure event-driven enterprise integrations using SAP BTP integration matrices." },
    { icon: "⚡", title: "Clean Core ABAP Development", desc: "Develop cloud-ready ABAP applications using standard RAP (RESTful Application Programming) and CAP models, keeping your core system completely upgrade-stable." },
    { icon: "📊", title: "Central Finance & Intelligent Ledger Sync", desc: "Unify highly fragmented regional financial instances into a centralized real-time transaction cockpit for single-source corporate close audits." },
    { icon: "📦", title: "Supply Chain & Extended Warehouse (EWM)", desc: "Optimize inventory movements, automate automated goods processing routes, and trace production operations from supplier logs down to consumer fulfillment." },
    { icon: "👁️", title: "Fiori UI/UX Modernization Layouts", desc: "Replace outdated SAP GUI screens with responsive, role-based HTML5 SAP Fiori applications customized for mobile and desktop field workflows." },
    { icon: "🛡️", title: "Governance, Risk & Compliance (GRC)", desc: "Incorporate strict segregation of duties (SoD), manage systemic operational risks, and automate access reviews to pass strict compliance testing." }
  ];

  const advancedServices = [
    { icon: "🔄", title: "Automated IDoc & RFC Integration Layers", desc: "Establish ultra-reliable, real-time message exchange channels connecting core modules cleanly to external non-SAP platforms and proprietary web layers." },
    { icon: "🔍", title: "Advanced Process Insights & Mining", desc: "Run background analytics over transactional log tables to locate process bottlenecks, payment leaks, and operational compliance gaps." },
    { icon: "💼", title: "SAP SuccessFactors Integration Hubs", desc: "Bridge structural corporate workforce records and strategic global talent changes seamlessly into centralized enterprise asset files." },
    { icon: "⚖️", title: "Dynamic Master Data Governance (MDG)", desc: "Deploy central validation blocks to govern, standardize, and duplicate vendor, customer, and material master attributes across business units." },
    { icon: "🚨", title: "Predictive Analytics & HANA Computing", desc: "Leverage in-memory database acceleration structures to build real-time inventory threshold monitors and predictive resource calculators." },
    { icon: "🗃️", title: "Ariba Procurement Network Unifications", desc: "Streamline external B2B purchasing routines by tying global vendor catalogs and dynamic contract handling modules into core accounts payable scripts." },
    { icon: "📝", title: "Tax Compliance & Document Orchestration", desc: "Automate dynamic regional calculation frameworks to generate localized tax filings and electronic billing formats safely." },
    { icon: "🔗", title: "Multi-Cloud Transport Management Systems", desc: "Coordinate software release versions across sandbox, quality assurance, and active deployment tiers using automated cloud pipeline gates." },
    { icon: "🧪", title: "Continuous Automated Regression Testing", desc: "Execute comprehensive automated unit test matrices over your custom software adaptations to verify core system stability prior to version updates." }
  ];

  const technologies = {
    "Core ERP Platforms": ["SAP S/4HANA Cloud / On-Premise", "SAP ECC 6.0 (EHP7/8) Environments", "SAP Central Finance Infrastructure", "SAP BW/4HANA Analytics Matrices"],
    "Development & Architecture": ["ABAP Cloud / RAP / CAP Frameworks", "SAP Fiori / UI5 / HTML5 Layouts", "Core Data Services (CDS) Views", "SAP GUI / Web Dynpro Elements"],
    "Integration & Platform BTP": ["SAP BTP Integration Suite", "SAP Advanced Event Mesh", "OData RESTful APIs / SOAP Engines", "ALE / IDoc / RFC System Conduits"],
    "Data Governance & Insights": ["SAP MDG Master Data Architecture", "SAP Signavio Process Transformation", "SAP Analytics Cloud (SAC)", "Master Data Management (MDM) Hubs"],
    "Security & Releases": ["SAP GRC Access Control Blocks", "SAP Cloud ALM / Solution Manager", "CTS+ / Transport Management Systems", "SAP Identity Authentication Service"]
  };

  const processSteps = [
    { num: "01", title: "System Discovery & Readiness", desc: "Auditing custom code footprint using SAP Readiness Check tools, evaluating simplification items, and plotting target migration scope." },
    { num: "02", title: "Enterprise Target Blueprinting", desc: "Mapping core functional modules, designing structural ledger schema blocks, and pinning data governance boundaries." },
    { num: "03", title: "System Copy & Core Ingestion", desc: "Executing initial data extracts, building sandbox test landscapes, and running test transform scripts." },
    { num: "04", title: "Clean Core Custom Extensions", desc: "Refactoring legacy custom elements into decoupled SAP BTP side-by-side logic blocks and modernizing interfaces." },
    { num: "05", title: "Validation Sprints & Load Stressing", desc: "Running data reconciliation cross-checks, testing multi-currency accounting logic, and tuning operational compute times." },
    { num: "06", title: "System Cutover & Live Deployment", desc: "Executing critical differential balances transfers, switching active transaction lines, and verifying single sign-on (SSO) links." },
    { num: "07", title: "Hypercare & Core Alignment", desc: "Monitoring background job processing flows, resolving transactional user errors, and adjusting batch schedules for runtime stability." }
  ];

  const industries = [
    "Global Manufacturing & Industrial Operations", "High-Volume Automotive & Component Assembly", "Complex Consumer Packaged Goods & Retail",
    "Pharmaceutical, Chemical & Life Science Hubs", "Energy, Utilities & Raw Resource Logistics", "Cross-Border Banking, Finance & Insurance",
    "High-Density Warehouse & Distribution Networks", "Aerospace, Defense & Heavy Equipment Systems", "Public Sector Government & Council Entities",
    "Telecommunications & Digital Services Providers", "Commercial Engineering & Construction Groups", "High-Velocity Commodity & Raw Materials Trading"
  ];

  const designBenefits = [
    "Single-Source Real-Time Financial Reconciliations", "Up to 50% Reduction in System DB Storage Footprint", "100% Core Upgrade-Stable Cloud Architectures",
    "Drastic Reductions in Supply Chain Cycle Latencies", "Strict Segregation of Duties Regulatory Compliance", "Streamlined, Role-Based Modern User Experiences",
    "Total Transactional Scaling Across Global Subsidiaries", "Automated Validation of Enterprise Master Records"
  ];

  const whyChooseUs = [
    "Certified SAP Solution Architects & Experts", "Proven Scale in S/4HANA Cloud Transformations", "Airtight Clean Core Code Customization Models",
    "Deep Competency in SAP BTP Integration Methods", "Platform-Agnostic Hybrid Infrastructure Implementations", "Resilient Performance Tuning & Database Design",
    "End-to-End Release Management & Governance Ownership", "Transparent Functional Deliverable Sprint Metrics", "High-Availability Support Maintenance Agreements",
    "Experienced ABAP Cloud & Fiori Frontend Engineers", "Iterative Proof-of-Concept Prototype Validation", "100% Transactional Ledger Stability Assurances"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          SAP Enterprise Systems Services | SAP Consulting & Implementation | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="SAP Enterprise Systems Services | SAP Consulting & Implementation | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides SAP Enterprise Systems services, including SAP consulting, SAP implementation, SAP S/4HANA migration, SAP ERP, SAP integration, SAP support, SAP customization, and enterprise digital transformation solutions."
        />

        <meta
          name="keywords"
          content="SAP Enterprise Systems, SAP Services, SAP Consulting, SAP Implementation, SAP S/4HANA, SAP ERP, SAP Migration, SAP Integration, SAP Support, SAP Customization, SAP HANA, SAP Fiori, Enterprise Resource Planning, SAP Digital Transformation, SAP Managed Services, Enterprise SAP Solutions, SAP Consulting Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/sap-enterprise-systems"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional SAP Enterprise Systems Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Accelerate your business transformation with SAP Enterprise Systems services from Affsoft IT Solutions Pvt. Ltd. We specialize in SAP consulting, S/4HANA migration, SAP ERP implementation, integration, and managed support."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/sap-enterprise-systems"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="SAP Enterprise Systems Services | SAP Consulting & Implementation | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Transform your enterprise with SAP Enterprise Systems services from Affsoft IT Solutions Pvt. Ltd. We provide SAP consulting, SAP ERP, SAP S/4HANA migration, integration, customization, and ongoing support."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="sap-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0f1c3f' // Deep sapphire/enterprise fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>SAP &nbsp; Enterprise &nbsp; Systems &nbsp; &amp; &nbsp;</span>
              <span>Digital Core Architectures </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-amber fs-3" style={{ color: '#f59e0b' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">SAP Enterprise Systems</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the complexity that locks up global operational workflows. Our SAP engineering methodology coordinates clean S/4HANA system migrations, couples decoupled extensions using cloud BTP integration suites, and enforces rigid master data architectures to convert heavy systems into fast strategic engines.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Scope an SAP Roadmap</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#f59e0b' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#f59e0b' }}>Enterprise Operations Tuning</span>
              <h2 className="fw-bold text-dark display-6">Deploy Resilient S/4HANA Transformations & High-Speed Data Conduits</h2>
              <p className="text-muted mt-3">
                Bloated, unmaintainable custom code additions, broken batch integration queues, and legacy data tables that complicate database performance slow down modern business agility. At Affsoft IT Solutions Pvt. Ltd., we construct secure, clean core, and systematically tracked global instances.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#f59e0b' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise SAP Upgrades</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#f59e0b' }}>Distributed Extension Layers</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Core Capabilities Tailored for High-Volume Processing</h2>
              <p className="text-muted mt-3">
                We extend standard database tables into highly resilient cross-platform logic patterns. From configuring secure multi-cloud deployment setups to executing deep data analytics, our custom extensions protect transaction consistency.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#f59e0b' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#fbbf24' }}>SAP Architecture Matrix</span>
              <h2 className="fw-bold display-6">Enterprise Infrastructure & Compute Toolsets We Unify</h2>
              <p className="text-light-50">We build, optimize, and manage enterprise operational networks using modern ecosystem engines.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#fbbf24' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#fbbf24' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Implementation Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered SAP Activate Framework</h2>
              <p className="text-muted">A clear, mathematically disciplined delivery roadmap designed to transition heavy core processes safely from validation passes onto active live production tracking.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#f59e0b' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#f59e0b' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We pair precise target field mappings with clean architecture software methods to ensure predictable core integration outcomes.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#f59e0b' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default SAPEnterpriseSystems;